import React, { useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbCheckbox,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbSelect,
  WxbTextarea,
} from '../wxb-ui';
import {
  createAutoAttributeCode,
  displayAttributeCode,
  formatAttributeOptionLabel,
  isAutoAttributeCode,
} from './attributeCodeGeneration';
import { ATTRIBUTE_KIND_LABELS } from './equipmentStateCatalogModel';
import TemporalExpressionEditor, { createElapsedExpression } from './TemporalExpressionEditor';
import type {
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentStateAttributeKind,
  EquipmentStateSemanticState,
  EquipmentStateTemporalEventReference,
  EquipmentStateTemporalTransition,
  EquipmentStateValue,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  open: boolean;
  readOnly?: boolean;
  initialAttribute: EquipmentStateAttribute | null;
  existingCodes: string[];
  /** Used only for a non-default refresh event; planned operation pairing stays in the action editor. */
  attributes: EquipmentStateAttribute[];
  /** @deprecated retained only to keep callers compatible; this editor no longer selects actions. */
  actions?: EquipmentStateAction[];
  onCancel: () => void;
  onSave: (attribute: EquipmentStateAttribute) => void;
}

const SEMANTIC_STATES: Array<{ value: EquipmentStateSemanticState; label: string }> = [
  { value: 'idle', label: 'idle · 空闲可用' },
  { value: 'processing', label: 'processing · 生产中' },
  { value: 'cleaning_cip', label: 'cleaning_cip · CIP 中' },
  { value: 'sterilizing_sip', label: 'sterilizing_sip · SIP 中' },
  { value: 'dirty_hold', label: 'dirty_hold · 脏停放' },
  { value: 'clean_hold', label: 'clean_hold · 洁净保持' },
  { value: 'changeover', label: 'changeover · 切换中' },
  { value: 'maintenance', label: 'maintenance · 维护中' },
];

const newValue = (): EquipmentStateValue => ({
  code: '',
  name: '',
  semanticState: null,
  isInitial: false,
  note: null,
});

const directTemporalStartEvent = (attributeCode: string, validValueCode: string) => ({
  kind: 'STATE_ENTRY' as const,
  attributeCode: attributeCode.trim().toUpperCase(),
  valueCode: validValueCode.trim().toUpperCase(),
});

const createRefreshEvent = (
  kind: 'STATE_ENTRY' | 'CLASS_ACTIVATION' | 'RESOURCE_COMMISSIONED_AT' | 'ACTION_STATUS' = 'STATE_ENTRY',
): EquipmentStateTemporalEventReference => {
  if (kind === 'STATE_ENTRY') return { kind, attributeCode: '', valueCode: '' };
  if (kind === 'ACTION_STATUS') return { kind, actionCode: '', status: 'RELEASED' };
  return { kind };
};

const REFRESH_EVENT_OPTIONS = [
  { value: 'STATE_ENTRY', label: '进入指定状态' },
  { value: 'RESOURCE_COMMISSIONED_AT', label: '设备投运' },
  { value: 'CLASS_ACTIVATION', label: '设备类别启用' },
  { value: 'ACTION_STATUS', label: '实际动作达到状态' },
];

const hasLegacyTemporalActionTrigger = (transition: EquipmentStateTemporalTransition): boolean => (
  transition.startRule.event.kind === 'ACTION_EFFECT'
  || (transition.refreshRule.kind === 'RESET_ON_EVENT'
    && transition.refreshRule.event.kind === 'ACTION_EFFECT')
);

const temporalConstraintCode = (attributeCode: string): string => {
  const code = attributeCode.trim().toUpperCase();
  return isAutoAttributeCode(code) ? '' : `EQUIPMENT_${code || 'PERIODIC_STATE'}_EXPIRED`;
};

const createTemporalTransition = (
  attributeCode: string,
  values: EquipmentStateValue[],
): EquipmentStateTemporalTransition => {
  const expiredValueCode = values.find((value) => value.isInitial)?.code || values[0]?.code || 'UNCHECKED';
  const validValueCode = values.find((value) => value.code !== expiredValueCode)?.code || 'CHECKED';
  return {
    validValueCode,
    expiredValueCode,
    startRule: { kind: 'EVENT', event: directTemporalStartEvent(attributeCode, validValueCode) },
    refreshRule: { kind: 'RESET_ON_START_EVENT' },
    expiryRule: {
      kind: 'TEMPORAL_EXPRESSION',
      expression: { ...createElapsedExpression(), value: 90, unit: 'DAY' },
    },
    enforcement: {
      constraintCode: temporalConstraintCode(attributeCode),
      severity: 'critical',
      hardOrSoft: 'hard',
      violationMessageTemplate: '资源 {resourceCode} 的周期状态已于 {expiredAt} 过期。',
    },
  };
};

const emptyAttribute = (code = ''): EquipmentStateAttribute => ({
  code,
  name: '',
  kind: 'DISCRETE',
  unit: null,
  initialNumber: null,
  minimum: null,
  maximum: null,
  clockKind: null,
  temporalExpression: null,
  temporalTransition: null,
  timeDrivenTransitions: [],
  note: null,
  values: [
    { ...newValue(), code: 'INITIAL', name: '初始', isInitial: true },
    { ...newValue(), code: 'READY', name: '就绪' },
  ],
});

const clone = (attribute: EquipmentStateAttribute): EquipmentStateAttribute => (
  JSON.parse(JSON.stringify(attribute)) as EquipmentStateAttribute
);

const EquipmentAttributeModal: React.FC<Props> = ({
  open,
  readOnly = false,
  initialAttribute,
  existingCodes,
  attributes,
  actions = [],
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState<EquipmentStateAttribute>(emptyAttribute());
  const [error, setError] = useState<string | null>(null);
  const existingCodeSignature = existingCodes
    .map((code) => code.trim().toUpperCase())
    .sort()
    .join('|');
  const nextAutoAttributeCode = useMemo(() => createAutoAttributeCode(
    existingCodeSignature ? existingCodeSignature.split('|') : [],
  ), [existingCodeSignature]);

  useEffect(() => {
    if (!open) return;
    const initial = initialAttribute
      ? clone(initialAttribute)
      : emptyAttribute(nextAutoAttributeCode);
    setDraft(initial);
    setError(null);
  }, [initialAttribute, nextAutoAttributeCode, open]);

  const valuesByCode = useMemo(() => new Set(draft.values.map((value) => value.code.trim().toUpperCase())), [draft.values]);

  const setKind = (kind: EquipmentStateAttributeKind) => {
    setDraft((current) => ({
      ...current,
      kind,
      unit: kind === 'COUNTER' ? (current.unit ?? 'cycle') : null,
      initialNumber: kind === 'COUNTER' ? (current.initialNumber ?? 0) : null,
      minimum: kind === 'COUNTER' ? current.minimum : null,
      maximum: kind === 'COUNTER' ? current.maximum : null,
      clockKind: kind === 'CLOCK' ? (current.clockKind ?? 'OTHER') : null,
      temporalExpression: kind === 'CLOCK'
        ? current.temporalExpression ?? createElapsedExpression()
        : null,
      temporalTransition: kind === 'DISCRETE' ? current.temporalTransition : null,
      timeDrivenTransitions: kind === 'DISCRETE' ? current.timeDrivenTransitions ?? [] : [],
      values: kind === 'DISCRETE' ? (current.values.length > 0 ? current.values : emptyAttribute().values) : [],
    }));
  };

  const updateValue = (index: number, patch: Partial<EquipmentStateValue>) => {
    setDraft((current) => ({
      ...current,
      values: current.values.map((value, itemIndex) => {
        if (itemIndex !== index) return patch.isInitial ? { ...value, isInitial: false } : value;
        return { ...value, ...patch };
      }),
    }));
  };

  const removeValue = (index: number) => {
    setDraft((current) => ({
      ...current,
      values: current.values.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateTransition = (patch: Partial<EquipmentStateTemporalTransition>) => {
    setDraft((current) => current.temporalTransition ? {
      ...current,
      temporalTransition: { ...current.temporalTransition, ...patch },
    } : current);
  };

  const setPeriodicState = (enabled: boolean) => {
    setDraft((current) => {
      if (enabled && current.values.length !== 2) {
        setError('两态周期仅支持有效值和到期值两个状态；多状态清洗/灭菌有效期请在“清洗灭菌路径”中维护。');
        return current;
      }
      const isUntouchedDefault = current.values.length === 2
        && current.values[0]?.code === 'INITIAL'
        && current.values[1]?.code === 'READY';
      const values = enabled && isUntouchedDefault
        ? [
          { ...newValue(), code: 'UNCHECKED', name: '未检查 / 已过期', isInitial: true },
          { ...newValue(), code: 'CHECKED', name: '检查有效', isInitial: false },
        ]
        : current.values;
      return {
        ...current,
        values,
        temporalTransition: enabled
          ? current.temporalTransition ?? createTemporalTransition(current.code, values)
          : null,
      };
    });
  };

  const validateRefreshEvent = (event: EquipmentStateTemporalEventReference): string | null => {
    if (event.kind === 'ACTION_STATUS') {
      return actions.some((action) => action.actionCode === event.actionCode)
        ? null
        : '实际动作刷新事件需要选择当前类别中已声明的状态动作。';
    }
    if (event.kind !== 'STATE_ENTRY') return null;
    const eventAttributeCode = event.attributeCode.trim().toUpperCase();
    const currentCode = draft.code.trim().toUpperCase();
    const eventAttribute = eventAttributeCode === currentCode
      ? draft
      : attributes.find((attribute) => attribute.code.trim().toUpperCase() === eventAttributeCode);
    if (!eventAttribute || eventAttribute.kind !== 'DISCRETE'
      || !eventAttribute.values.some((value) => value.code.trim().toUpperCase() === event.valueCode.trim().toUpperCase())) {
      return '指定刷新事件需要选择已声明的离散状态及其状态值。';
    }
    if (eventAttributeCode === currentCode
      && event.valueCode.trim().toUpperCase() !== draft.temporalTransition?.validValueCode.trim().toUpperCase()) {
      return '本周期状态作为刷新事件时只能进入有效状态；到期状态不能重置计时。';
    }
    return null;
  };

  const submit = () => {
    const code = draft.code.trim().toUpperCase();
    const hasSystemGeneratedCode = isAutoAttributeCode(code);
    if ((!hasSystemGeneratedCode && !code) || !draft.name.trim()) {
      setError('请填写属性名称。');
      return;
    }
    if (draft.kind === 'DISCRETE') {
      if (draft.values.length === 0 || draft.values.some((value) => !value.code.trim() || !value.name.trim())) {
        setError('离散属性至少需要一个填写完整的状态值。');
        return;
      }
      if (draft.values.filter((value) => value.isInitial).length !== 1) {
        setError('离散属性必须且只能有一个初始值。');
        return;
      }
      const codes = draft.values.map((value) => value.code.trim().toUpperCase());
      if (new Set(codes).size !== codes.length) {
        setError('同一属性内的状态值编码不能重复。');
        return;
      }
    }
    if (draft.kind === 'COUNTER' && (!draft.unit?.trim() || draft.initialNumber == null)) {
      setError('计数属性需要计量单位和初始值。');
      return;
    }
    if (draft.kind === 'CLOCK' && (!draft.clockKind || !draft.temporalExpression)) {
      setError('期限时钟需要时钟语义和完整的过期计算方式。');
      return;
    }

    const transition = draft.temporalTransition;
    const validValueCode = transition?.validValueCode.trim().toUpperCase() ?? '';
    const expiredValueCode = transition?.expiredValueCode.trim().toUpperCase() ?? '';
    if (transition) {
      if (draft.values.length !== 2) {
        setError('两态周期仅支持有效值和到期值两个状态；多状态有效期请在“清洗灭菌路径”中维护。');
        return;
      }
      if (!valuesByCode.has(validValueCode) || !valuesByCode.has(expiredValueCode)
        || validValueCode === expiredValueCode) {
        setError('周期状态的有效值和到期值必须是两个不同的已声明状态值。');
        return;
      }
      if (transition.refreshRule.kind === 'RESET_ON_EVENT') {
        const refreshError = validateRefreshEvent(transition.refreshRule.event);
        if (refreshError) {
          setError(refreshError);
          return;
        }
      }
      if ((!transition.enforcement.constraintCode.trim() && !hasSystemGeneratedCode)
        || !transition.enforcement.violationMessageTemplate.trim()) {
        setError('请填写周期状态的约束编码和诊断说明。');
        return;
      }
      if (!/\{(?:resourceId|resourceCode)\}/.test(transition.enforcement.violationMessageTemplate)
        || !/\{(?:dueAt|expiredAt|timeWindow)\}/.test(transition.enforcement.violationMessageTemplate)) {
        setError('诊断说明需包含资源占位符和到期时刻占位符，例如 {resourceCode}、{expiredAt}。');
        return;
      }
    }

    const normalizedValues = draft.kind === 'DISCRETE' ? draft.values.map((value) => ({
      ...value,
      code: value.code.trim().toUpperCase(),
      name: value.name.trim(),
      note: value.note?.trim() || null,
      isInitial: transition ? value.code.trim().toUpperCase() === expiredValueCode : value.isInitial,
    })) : [];
    onSave({
      ...draft,
      code,
      name: draft.name.trim(),
      unit: draft.unit?.trim() || null,
      note: draft.note?.trim() || null,
      temporalTransition: transition ? {
        ...transition,
        validValueCode,
        expiredValueCode,
        startRule: { kind: 'EVENT', event: directTemporalStartEvent(code, validValueCode) },
        refreshRule: transition.refreshRule.kind === 'RESET_ON_EVENT'
          && transition.refreshRule.event.kind === 'ACTION_EFFECT'
          ? { kind: 'RESET_ON_START_EVENT' }
          : transition.refreshRule,
        enforcement: {
          ...transition.enforcement,
          constraintCode: transition.enforcement.constraintCode.trim().toUpperCase(),
          violationMessageTemplate: transition.enforcement.violationMessageTemplate.trim(),
        },
      } : null,
      values: normalizedValues,
    });
  };

  const transition = draft.temporalTransition;
  const hasLegacyActionTrigger = Boolean(transition && hasLegacyTemporalActionTrigger(transition));
  const hasPendingAttributeCode = isAutoAttributeCode(draft.code);
  const generatedCodePreview = draft.name.trim()
    ? '保存类别模型后由系统分配'
    : '填写属性名称后可保存并由系统分配';
  const stateValueOptions = draft.values.map((value) => ({
    value: value.code.trim().toUpperCase(),
    label: `${value.name || '未命名'} (${value.code.trim().toUpperCase() || '—'})`,
  })).filter((option) => option.value);
  const refreshStateAttributes = [
    draft,
    ...attributes.filter((attribute) => (
      attribute.kind === 'DISCRETE'
      && attribute.code.trim().toUpperCase() !== draft.code.trim().toUpperCase()
    )),
  ].filter((attribute) => attribute.kind === 'DISCRETE');
  const refreshEvent = transition && transition.refreshRule.kind === 'RESET_ON_EVENT'
    ? transition.refreshRule.event
    : null;
  const selectedRefreshStateAttribute = refreshEvent?.kind === 'STATE_ENTRY'
    ? refreshStateAttributes.find((attribute) => (
      attribute.code.trim().toUpperCase() === refreshEvent.attributeCode.trim().toUpperCase()
    ))
    : null;

  return (
    <WxbModal
      open={open}
      title={readOnly ? '查看状态与周期定义' : initialAttribute ? '编辑状态与周期定义' : '添加状态与周期定义'}
      okText={initialAttribute ? '保存定义' : '添加定义'}
      onCancel={onCancel}
      onOk={submit}
      footer={readOnly ? (
        <div className="esc-drawer-actions">
          <WxbButton type="button" variant="ghost" onClick={onCancel}>关闭</WxbButton>
        </div>
      ) : undefined}
      width={960}
      destroyOnClose
    >
      <div className="esc-modal-stack">
        {error && <WxbAlert variant="error" title="定义尚不能保存">{error}</WxbAlert>}
        <div className="esc-form-grid esc-form-grid-three">
          <WxbInput
            label={hasPendingAttributeCode ? '属性编码（系统生成）' : '属性编码'}
            value={hasPendingAttributeCode ? generatedCodePreview : displayAttributeCode(draft.code)}
            disabled
            helpText={hasPendingAttributeCode
              ? '保存类别模型时由系统生成稳定编码；当前预览和内部临时标识不会成为正式编码。'
              : '稳定编码创建后不可修改。'}
          />
          <WxbInput
            label="属性名称"
            value={draft.name}
            disabled={readOnly}
            placeholder="例如 洁净状态"
            onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
          />
          <WxbSelect
            label="属性类型"
            value={draft.kind}
            disabled={readOnly}
            options={Object.entries(ATTRIBUTE_KIND_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(value) => setKind(value as EquipmentStateAttributeKind)}
          />
        </div>

        {draft.kind === 'DISCRETE' && (
          <>
            <div className="esc-subsection">
              <div className="esc-subsection-header">
                <div>
                  <h3>状态值</h3>
                  <p>必须且只能有一个初始值；标准语义用于排产诊断，就绪规则在类别级单独维护。</p>
                </div>
                {!readOnly && (!transition || draft.values.length < 2) && (
                  <WxbButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setDraft((current) => ({ ...current, values: [...current.values, newValue()] }))}
                  >
                    添加状态值
                  </WxbButton>
                )}
              </div>
              <div className="esc-value-list">
                {draft.values.map((value, index) => (
                  <div className="esc-value-row" key={`state-value-${index}`}>
                    <WxbInput
                      aria-label={`状态值 ${index + 1} 编码`}
                      value={value.code}
                      disabled={readOnly}
                      placeholder="值编码"
                      onChange={(event) => updateValue(index, { code: event.target.value })}
                    />
                    <WxbInput
                      aria-label={`状态值 ${index + 1} 名称`}
                      value={value.name}
                      disabled={readOnly}
                      placeholder="显示名称"
                      onChange={(event) => updateValue(index, { name: event.target.value })}
                    />
                    <WxbSelect
                      aria-label={`状态值 ${index + 1} 标准语义`}
                      allowClear
                      disabled={readOnly}
                      value={value.semanticState ?? undefined}
                      options={SEMANTIC_STATES}
                      placeholder="标准语义（可选）"
                      onChange={(semanticState) => updateValue(index, {
                        semanticState: (semanticState as EquipmentStateSemanticState | undefined) ?? null,
                      })}
                    />
                    <div className="esc-value-flags">
                      <WxbCheckbox
                        checked={value.isInitial}
                        disabled={readOnly || Boolean(transition)}
                        onChange={(checked) => updateValue(index, { isInitial: checked })}
                      >
                        初始
                      </WxbCheckbox>
                    </div>
                    {!readOnly && (!transition || draft.values.length > 2) && (
                      <WxbButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeValue(index)}
                        aria-label={`删除状态值 ${index + 1}`}
                      >
                        删除
                      </WxbButton>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="esc-subsection">
              <div className="esc-subsection-header">
                <div>
                  <h3>两态周期（兼容）</h3>
                  <p>仅用于严格的“有效 / 到期”两状态定义。清洗、SIP、生产等多状态生命周期请在主工作区的“清洗灭菌路径”中维护自动到期边。</p>
                </div>
                <WxbCheckbox
                  checked={Boolean(transition)}
                  disabled={readOnly}
                  onChange={setPeriodicState}
                >
                  作为两态周期维护
                </WxbCheckbox>
              </div>
              {transition && (
                <div className="esc-modal-stack">
                  <WxbAlert title="周期状态由时间驱动">
                    操作在计划开始或结束时直接写入本属性：写为有效状态会按刷新规则开始或重置计时；写为到期状态会立即失效并清除计时。APS 随排程投影未来状态，不等待人工完成确认。
                  </WxbAlert>
                  {hasLegacyActionTrigger && (
                    <WxbAlert title="检测到旧版计划动作触发">
                      该草稿保留了旧版 ACTION_EFFECT 触发。保存此状态属性时会迁移为“状态变为有效值（由动作直接写入）”；已发布版本不会被改写。
                    </WxbAlert>
                  )}
                  <div className="esc-form-grid">
                    <WxbSelect
                      label="有效状态"
                      value={transition.validValueCode || undefined}
                      disabled={readOnly}
                      options={stateValueOptions}
                      placeholder="选择周期内的有效状态"
                      onChange={(value) => {
                        const validValueCode = String(value);
                        updateTransition({
                          validValueCode,
                          startRule: {
                            kind: 'EVENT',
                            event: directTemporalStartEvent(draft.code, validValueCode),
                          },
                        });
                      }}
                    />
                    <WxbSelect
                      label="到期状态（也是初始状态）"
                      value={transition.expiredValueCode || undefined}
                      disabled={readOnly}
                      options={stateValueOptions}
                      placeholder="选择到期后状态"
                      onChange={(value) => {
                        const expiredValueCode = String(value);
                        updateTransition({ expiredValueCode });
                        setDraft((current) => ({
                          ...current,
                          values: current.values.map((stateValue) => ({
                            ...stateValue,
                            isInitial: stateValue.code.trim().toUpperCase() === expiredValueCode,
                          })),
                        }));
                      }}
                    />
                  </div>
                  <div className="esc-subsection">
                    <div className="esc-subsection-header">
                      <div>
                        <h3>1. 记时开始方式</h3>
                        <p>周期状态直接绑定自身的有效值，不在此重复选择操作或事件。</p>
                      </div>
                    </div>
                    <WxbInput
                      label="开始事件"
                      value={hasLegacyActionTrigger
                        ? '旧版计划动作触发（保存草稿后迁移）'
                        : '状态变为有效值（由动作直接写入）'}
                      disabled
                      helpText="在“动作”的发生后状态变化中选择本属性的有效状态；动作开始或结束时点由该动作本身定义。"
                    />
                  </div>
                  <div className="esc-subsection">
                    <div className="esc-subsection-header">
                      <div>
                        <h3>2. 状态刷新机制</h3>
                        <p>决定后续有效状态写入或指定状态事件是否重新计时；写入到期状态始终立即失效并清除计时。</p>
                      </div>
                    </div>
                    <WxbSelect
                      label="刷新方式"
                      value={transition.refreshRule.kind}
                      disabled={readOnly}
                      options={[
                        { value: 'RESET_ON_START_EVENT', label: '再次写入有效状态时重置' },
                        { value: 'RESET_ON_EVENT', label: '指定状态事件发生时重置' },
                        { value: 'ONE_SHOT', label: '只在首次写入有效状态时开始' },
                      ]}
                      onChange={(kind) => updateTransition({
                        refreshRule: kind === 'RESET_ON_EVENT'
                          ? { kind: 'RESET_ON_EVENT', event: createRefreshEvent() }
                          : { kind: kind as 'ONE_SHOT' | 'RESET_ON_START_EVENT' },
                      })}
                    />
                    {transition.refreshRule.kind === 'RESET_ON_EVENT' && (
                      <div className="esc-modal-stack">
                        {transition.refreshRule.event.kind === 'ACTION_EFFECT' ? (
                          <WxbAlert title="旧版计划动作刷新事件">
                            该事件将在保存草稿时迁移为“再次写入有效状态时重置”；新定义无需在属性中再次选择操作。
                          </WxbAlert>
                        ) : (
                          <>
                            <WxbSelect
                              label="指定刷新事件"
                              value={transition.refreshRule.event.kind}
                              disabled={readOnly}
                              options={REFRESH_EVENT_OPTIONS}
                              onChange={(kind) => updateTransition({
                                refreshRule: {
                                  kind: 'RESET_ON_EVENT',
                                  event: createRefreshEvent(kind as 'STATE_ENTRY' | 'CLASS_ACTIVATION' | 'RESOURCE_COMMISSIONED_AT' | 'ACTION_STATUS'),
                                },
                              })}
                            />
                            {refreshEvent?.kind === 'STATE_ENTRY' && (
                              <div className="esc-form-grid">
                                <WxbSelect
                                  label="刷新状态属性"
                                  value={refreshEvent.attributeCode || undefined}
                                  disabled={readOnly}
                                  options={refreshStateAttributes.map((attribute) => ({
                                    value: attribute.code,
                                    label: formatAttributeOptionLabel(attribute.name, attribute.code),
                                  })).filter((option) => option.value)}
                                  placeholder="选择离散状态属性"
                                  onChange={(attributeCode) => updateTransition({
                                    refreshRule: {
                                      kind: 'RESET_ON_EVENT',
                                      event: { kind: 'STATE_ENTRY', attributeCode: String(attributeCode), valueCode: '' },
                                    },
                                  })}
                                />
                                <WxbSelect
                                  label="刷新状态值"
                                  value={refreshEvent.valueCode || undefined}
                                  disabled={readOnly}
                                  options={(selectedRefreshStateAttribute?.values ?? [])
                                    .filter((value) => (
                                      selectedRefreshStateAttribute?.code.trim().toUpperCase() !== draft.code.trim().toUpperCase()
                                      || value.code.trim().toUpperCase() === transition.validValueCode.trim().toUpperCase()
                                    ))
                                    .map((value) => ({
                                      value: value.code,
                                      label: `${value.name || '未命名'} (${value.code || '—'})`,
                                    }))}
                                  placeholder="选择状态值"
                                  onChange={(valueCode) => updateTransition({
                                    refreshRule: {
                                      kind: 'RESET_ON_EVENT',
                                      event: {
                                        kind: 'STATE_ENTRY',
                                        attributeCode: refreshEvent.attributeCode,
                                        valueCode: String(valueCode),
                                      },
                                    },
                                  })}
                                />
                              </div>
                            )}
                            {refreshEvent?.kind === 'ACTION_STATUS' && (
                              <div className="esc-form-grid">
                                <WxbSelect
                                  label="实际状态动作"
                                  value={refreshEvent.actionCode || undefined}
                                  disabled={readOnly}
                                  options={actions.map((action) => ({
                                    value: action.actionCode,
                                    label: `${action.name} (${action.actionCode})`,
                                  }))}
                                  placeholder="选择状态动作"
                                  onChange={(actionCode) => updateTransition({
                                    refreshRule: {
                                      kind: 'RESET_ON_EVENT',
                                      event: {
                                        kind: 'ACTION_STATUS',
                                        actionCode: String(actionCode),
                                        status: refreshEvent.status,
                                      },
                                    },
                                  })}
                                />
                                <WxbSelect
                                  label="实际动作状态"
                                  value={refreshEvent.status}
                                  disabled={readOnly}
                                  options={[
                                    { value: 'COMPLETED', label: '已完成' },
                                    { value: 'RELEASED', label: '已放行' },
                                  ]}
                                  onChange={(status) => updateTransition({
                                    refreshRule: {
                                      kind: 'RESET_ON_EVENT',
                                      event: {
                                        kind: 'ACTION_STATUS',
                                        actionCode: refreshEvent.actionCode,
                                        status: status as 'COMPLETED' | 'RELEASED',
                                      },
                                    },
                                  })}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="esc-subsection">
                    <div className="esc-subsection-header">
                      <div>
                        <h3>3. 过期计算方式</h3>
                        <p>连续时长与日历期间语义不同；例如 90 连续天不等于 3 个自然月。</p>
                      </div>
                    </div>
                    <TemporalExpressionEditor
                      value={transition.expiryRule.expression}
                      disabled={readOnly}
                      labelPrefix="过期"
                      hideCalendarContext
                      onChange={(expression) => updateTransition({
                        expiryRule: { kind: 'TEMPORAL_EXPRESSION', expression },
                      })}
                    />
                  </div>
                  <div className="esc-subsection">
                    <div className="esc-subsection-header">
                      <div>
                        <h3>到期约束</h3>
                        <p>状态到期会形成排产诊断；硬约束应在类别就绪条件中要求本属性处于有效状态。</p>
                      </div>
                    </div>
                    <div className="esc-form-grid esc-form-grid-four">
                      <WxbInput
                        label="约束编码"
                        value={transition.enforcement.constraintCode}
                        disabled={readOnly}
                        placeholder={isAutoAttributeCode(draft.code)
                          ? '保存后系统生成'
                          : '例如 EQUIPMENT_REACTOR_CHECK_EXPIRED'}
                        helpText={isAutoAttributeCode(draft.code)
                          ? '留空时，系统会在生成正式属性编码后同步生成约束编码。'
                          : undefined}
                        onChange={(event) => updateTransition({
                          enforcement: { ...transition.enforcement, constraintCode: event.target.value },
                        })}
                      />
                      <WxbSelect
                        label="严重度"
                        value={transition.enforcement.severity}
                        disabled={readOnly}
                        options={[
                          { value: 'info', label: '提示' },
                          { value: 'warning', label: '警告' },
                          { value: 'critical', label: '严重' },
                        ]}
                        onChange={(severity) => updateTransition({
                          enforcement: { ...transition.enforcement, severity: severity as EquipmentStateTemporalTransition['enforcement']['severity'] },
                        })}
                      />
                      <WxbSelect
                        label="约束性质"
                        value={transition.enforcement.hardOrSoft}
                        disabled={readOnly}
                        options={[
                          { value: 'hard', label: '硬约束' },
                          { value: 'soft', label: '软约束' },
                        ]}
                        onChange={(hardOrSoft) => updateTransition({
                          enforcement: { ...transition.enforcement, hardOrSoft: hardOrSoft as EquipmentStateTemporalTransition['enforcement']['hardOrSoft'] },
                        })}
                      />
                    </div>
                    <WxbTextarea
                      label="诊断说明模板"
                      value={transition.enforcement.violationMessageTemplate}
                      disabled={readOnly}
                      placeholder="例如 资源 {resourceCode} 的反应器 Check 已于 {expiredAt} 过期。"
                      helpText="必须包含资源占位符和到期时刻占位符。"
                      onChange={(event) => updateTransition({
                        enforcement: { ...transition.enforcement, violationMessageTemplate: event.target.value },
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {draft.kind === 'COUNTER' && (
          <div className="esc-form-grid esc-form-grid-four">
            <WxbInput
              label="计量单位"
              value={draft.unit ?? ''}
              disabled={readOnly}
              placeholder="例如 cycle"
              onChange={(event) => setDraft((current) => ({ ...current, unit: event.target.value }))}
            />
            <WxbInputNumber
              label="初始值"
              disabled={readOnly}
              value={draft.initialNumber}
              onChange={(value) => setDraft((current) => ({
                ...current,
                initialNumber: typeof value === 'number' ? value : null,
              }))}
            />
            <WxbInputNumber
              label="最小值"
              disabled={readOnly}
              value={draft.minimum}
              onChange={(value) => setDraft((current) => ({
                ...current,
                minimum: typeof value === 'number' ? value : null,
              }))}
            />
            <WxbInputNumber
              label="最大值"
              disabled={readOnly}
              value={draft.maximum}
              onChange={(value) => setDraft((current) => ({
                ...current,
                maximum: typeof value === 'number' ? value : null,
              }))}
            />
          </div>
        )}

        {draft.kind === 'CLOCK' && (
          <div className="esc-subsection">
            <div className="esc-form-grid">
              <WxbSelect
                label="时钟语义"
                value={draft.clockKind ?? undefined}
                disabled={readOnly}
                options={[
                  { value: 'DHT', label: 'DHT · 脏停放上限' },
                  { value: 'CHT', label: 'CHT · 洁净保持上限' },
                  { value: 'OTHER', label: 'OTHER · 其他期限' },
                ]}
                onChange={(clockKind) => setDraft((current) => ({
                  ...current,
                  clockKind: clockKind as EquipmentStateAttribute['clockKind'],
                }))}
              />
            </div>
            {draft.temporalExpression && (
              <TemporalExpressionEditor
                value={draft.temporalExpression}
                disabled={readOnly}
                labelPrefix="时钟"
                hideCalendarContext
                onChange={(temporalExpression) => setDraft((current) => ({ ...current, temporalExpression }))}
              />
            )}
          </div>
        )}

        <WxbTextarea
          label="属性说明"
          disabled={readOnly}
          value={draft.note ?? ''}
          placeholder="记录适用边界、状态含义或计数口径。"
          onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
        />
      </div>
    </WxbModal>
  );
};

export default EquipmentAttributeModal;
